import { useRegistrant } from '@/hooks/useRegistrant'
import { useServiceWorker } from '@/hooks/useServiceWorker'
import { useCallback, useEffect, useState } from 'react'
import { PushController } from '@/controllers'
import { config } from '@/config'
import { StorageFactory } from '@/services'
import { StorageType } from '@/interfaces/storage'

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from(raw.split('').map((c) => c.charCodeAt(0)))
}

function isPushManagerExist() {
  return 'PushManager' in window
}

interface PushSubscriptionStoreItem {
  subscriptionId: number
}

interface DBMessage {
  registrantId: number
  title: string
  body: string
  timestamp: number
  notificationType: string
}

interface Message {
  registrantId: number
  title: string
  body: string
  timestamp: Date
  notificationType: string
}

const PUSH_SUBSCRIPTION_KEY = '__charleston_push_subscription'

const DB_NAME = 'notifications-db'
const DB_VERSION = 1
const MESSAGES_STORE = 'messages'
const MESSAGES_COUNT_STORE = 'messages-count'

// TODO: unify openDB method
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onerror = (event) => {
      // @ts-ignore
      reject(`Cannot connect to indexDB: ${event.target?.error?.message}`)
    }
    req.onsuccess = (event) => {
      // @ts-ignore
      resolve(event.target.result)
    }
    req.onupgradeneeded = (event) => {
      // @ts-ignore
      const db = event.target.result
      if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
        /**
         * Data structure:
         *     notificationType: string
         *     registrantId: number
         *     title: string
         *     body: string
         *     timestamp: number
         */
        db.createObjectStore(MESSAGES_STORE, { autoIncrement: true })
      }
      if (!db.objectStoreNames.contains(MESSAGES_COUNT_STORE)) {
        db.createObjectStore(MESSAGES_COUNT_STORE) // key = registrantId, value = count
      }
    }
  })
}

async function resetMessagesCount(registrantId: number) {
  const db = await openDb()
  const tx = db.transaction(MESSAGES_COUNT_STORE, 'readwrite')
  const store = tx.objectStore(MESSAGES_COUNT_STORE)
  store.put(0, registrantId)

  if ('clearAppBadge' in navigator) {
    await navigator.clearAppBadge()
  }
}

async function loadMessagesCount(registrantId: number) {
  const db = await openDb()
  return new Promise<number>((resolve, reject) => {
    const tx = db.transaction(MESSAGES_COUNT_STORE, 'readonly')
    const store = tx.objectStore(MESSAGES_COUNT_STORE)
    const req = store.get(registrantId)
    req.onsuccess = () => {
      resolve(req.result || 0)
    }
    req.onerror = (event) => {
      // @ts-ignore
      reject(event.target?.error?.message)
    }
  })
}

async function loadMessages(registrantId: number) {
  const db = await openDb()
  return new Promise<Message[]>((resolve, reject) => {
    const tx = db.transaction(MESSAGES_STORE, 'readonly')
    const store = tx.objectStore(MESSAGES_STORE)
    const req = store.getAll()
    req.onsuccess = () => {
      const dbMessages = (req.result as DBMessage[]).filter((m) => m.registrantId === registrantId)
      resolve(
        dbMessages.map((m) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        })),
      )
    }
    req.onerror = (event) => {
      // @ts-ignore
      reject(event.target?.error?.message)
    }
  })
}

function pushSubscriptionKey(registrantId: number) {
  return `${PUSH_SUBSCRIPTION_KEY}_${registrantId}`
}

function isRegistrantSubscribed(registrantId: number): boolean {
  const storage = StorageFactory.getStorage(StorageType.LOCAL)
  storage.removeItem(PUSH_SUBSCRIPTION_KEY)
  return Boolean(storage.getItem<PushSubscriptionStoreItem>(pushSubscriptionKey(registrantId)))
}

function setRegistrantSubscribed(registrantId: number, subscriptionId: number) {
  const storage = StorageFactory.getStorage(StorageType.LOCAL)
  storage.setItem<PushSubscriptionStoreItem>(pushSubscriptionKey(registrantId), {
    subscriptionId,
  })
}

export const usePushNotification = () => {
  const { swRegistration } = useServiceWorker()
  const { registrant } = useRegistrant()
  const [isReady, setReady] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (registrant) {
      setReady(true)
    }
  }, [registrant])

  useEffect(() => {
    if (registrant) {
      ;(async () => {
        const messages = await loadMessages(registrant.id)
        setMessages(messages)
      })()
    }
  }, [registrant])

  useEffect(() => {
    if (registrant) {
      ;(async () => {
        const messages = await loadMessages(registrant.id)
        setMessages(messages)
      })()
    }
  }, [registrant])

  useEffect(() => {
    if (registrant) {
      ;(async () => {
        const count = await loadMessagesCount(registrant.id)
        setUnreadCount(count)
      })()
    }
  }, [registrant])

  const markMessagesAsRead = useCallback(async () => {
    if (registrant) {
      await resetMessagesCount(registrant.id)
    }
  }, [registrant])

  const subscribeForPush = useCallback(async () => {
    if (swRegistration && registrant) {
      if (!isPushManagerExist() || !swRegistration.pushManager) {
        console.error('Push not supported in this context (Safari tab?)')
        return
      }
      if (!isRegistrantSubscribed(registrant.id)) {
        const subscription = await swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.push.vapidPublicKey!),
        })
        const {
          result: [_, { id: subscriptionId }],
        } = await PushController.subscribe({ registrantId: registrant.id, subscription })
        setRegistrantSubscribed(registrant.id, subscriptionId)
      }
    }
  }, [swRegistration, registrant])

  return {
    isReady,
    messages,
    unreadCount,
    markMessagesAsRead,
    subscribeForPush,
  }
}
